import type { DataProvider } from 'react-admin';
import type { VatInvoice } from '../types';
import { fetchAllPurchaseInvoices, mapMisaInvoiceRow } from './misaClient';

export interface InvoiceSyncSummary {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  invalid: number;
}

const digits = (value?: string) => (value || '').replace(/\D/g, '');
const normalizeIdentityText = (value?: string) =>
  String(value || '').trim().toLocaleLowerCase('vi-VN').replace(/\s+/g, ' ');

/** MISA can return one invoice number with different voucher dates. */
export const invoiceIdentityKey = (invoice: Pick<VatInvoice, 'taxCode' | 'invoiceNumber' | 'supplierName' | 'invoiceDate'>) => {
  const number = normalizeIdentityText(invoice.invoiceNumber);
  const supplier = normalizeIdentityText(invoice.supplierName);
  const taxCode = digits(invoice.taxCode);
  return `${taxCode || supplier}|${number}`;
};

export function isImportableMisaInvoice(row: ReturnType<typeof mapMisaInvoiceRow>): boolean {
  return !!row.invoiceNumber.trim() &&
    !!row.supplierName.trim() &&
    /^\d{4}-\d{2}-\d{2}$/.test(row.invoiceDate) &&
    Number.isFinite(row.amount) &&
    row.amount >= 0;
}

export async function syncPurchaseInvoicesFromMisa(
  dp: DataProvider,
  options?: { fromISO?: string; toISO?: string },
): Promise<InvoiceSyncSummary> {
  const remote = await fetchAllPurchaseInvoices({ ...options, pageSize: 100 });
  const mapped = remote.rows.map(mapMisaInvoiceRow);
  const valid = mapped.filter(isImportableMisaInvoice);
  const { data: local } = await dp.getList<VatInvoice>('vatInvoices', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'invoiceDate', order: 'DESC' },
    filter: {},
  });
  const byKey = new Map(local.map((invoice) => [invoiceIdentityKey(invoice), invoice]));
  const summary: InvoiceSyncSummary = {
    total: mapped.length,
    created: 0,
    updated: 0,
    skipped: 0,
    invalid: mapped.length - valid.length,
  };
  for (const invoice of valid) {
    const existing = byKey.get(invoiceIdentityKey(invoice));
    if (!existing) {
      const { data } = await dp.create<VatInvoice>('vatInvoices', { data: invoice as Omit<VatInvoice, 'id'> });
      byKey.set(invoiceIdentityKey(data), data);
      summary.created++;
      continue;
    }
    const changed =
      existing.supplierName !== invoice.supplierName ||
      existing.amount !== invoice.amount ||
      existing.taxCode !== invoice.taxCode ||
      existing.invoiceDate !== invoice.invoiceDate;
    if (!changed) {
      summary.skipped++;
      continue;
    }
    await dp.update('vatInvoices', {
      id: existing.id,
      data: { ...existing, ...invoice },
      previousData: existing,
    });
    summary.updated++;
  }
  return summary;
}
